<script setup lang="ts">
import { computed, onMounted, ref, resolveComponent } from 'vue';
import { VideoEmbedVariant, getVideoEmbedUrl } from '@pkg/utilities/video';
import { VIMEO_URL_REGEX } from '@pkg/utilities/video/types';
import { VideoType } from '@pkg/sanity-utils/constants/video';
import { useCookieConsent } from '@pkg/ui/composables/useCookieConsent';
import type { Image } from '@sanity/types';

interface Props {
  url: string;
  webm?: string;
  ogg?: string;
  fallbackImage?: Image;
  fetchpriority?: 'high';
  controls?: boolean;
  autoplay?: boolean;
  videoType?: VideoType;
}

const props = defineProps<Props>();

const { functional: hasFunctionalConsent } = useCookieConsent('functional');

const isVimeo = computed(() => VIMEO_URL_REGEX.test(props.url));

// Vimeo embeds may only load once functional-cookie consent is granted. Until then
// we render nothing and let the fallback image behind this component show through,
// while also suppressing the Vimeo resource hints so no connection is opened.
const isConsentGated = computed(() => isVimeo.value && !hasFunctionalConsent.value);

const embedSrc = computed(() => getVideoEmbedUrl({ videoUrl: props.url, variant: VideoEmbedVariant.Background, controls: props.controls ?? true, autoplay: props.autoplay ?? false }));

const nativeControls = computed(() => props.controls ?? false);
const nativeMuted = computed(() => !nativeControls.value);
const nativeAutoplay = computed(() => (nativeControls.value ? false : props.autoplay ?? true));

const videoRef = ref<HTMLVideoElement | null>(null);
onMounted(() => {
  if (videoRef.value) {
    videoRef.value.muted = nativeMuted.value;
  }
});

/** Host app registers BaseImage (Nuxt component dir). */
const BaseImage = resolveComponent('BaseImage');

// Preconnects, the player script preload and the embed preload all reach Vimeo
// domains, so they are only emitted for Vimeo once functional consent is granted.
useHead(() => {
  if (!isVimeo.value || !hasFunctionalConsent.value) {
    return {};
  }

  const link: Array<Record<string, string | undefined>> = [
    { rel: 'preconnect', href: 'https://player.vimeo.com', id: 'https://player.vimeo.com' },
    { rel: 'preconnect', href: 'https://f.vimeocdn.com', id: 'https://f.vimeocdn.com' },
    { rel: 'preconnect', href: 'https://i.vimeocdn.com', id: 'https://i.vimeocdn.com' },
  ];

  if (props.fetchpriority === 'high') {
    link.push({
      rel: 'preload',
      href: 'https://f.vimeocdn.com/p/4.29.1/js/player.module.js',
      as: 'script',
      fetchpriority: 'high',
      crossorigin: 'anonymous',
      id: 'https://f.vimeocdn.com/p/4.29.1/js/player.module.js',
    });

    if (typeof embedSrc.value === 'string') {
      link.push({
        rel: 'preload',
        href: embedSrc.value,
        as: 'fetch',
        fetchpriority: 'high',
        crossorigin: 'anonymous',
        id: embedSrc.value,
      });
    }
  }

  return { link };
});
</script>

<template>
  <div v-if="videoType === VideoType.HOSTED" class="c-video-background__container">
    <video
      ref="videoRef"
      :autoplay="nativeAutoplay"
      :muted="nativeMuted"
      :controls="nativeControls"
      loop
      playsinline
      class="c-video-background__native"
    >
      <source :src="url" type="video/mp4">
      <source v-if="webm" :src="webm" type="video/webm">
      <source v-if="ogg" :src="ogg" type="video/ogg">
      Your browser does not support the video tag
    </video>
  </div>
  <component
    :is="BaseImage"
    v-else-if="isConsentGated && fallbackImage"
    :image="fallbackImage"
    class="c-video-background__fallback"
  />
  <div v-else-if="!isConsentGated && embedSrc" class="c-video-background__container">
    <iframe
      :src="embedSrc"
      title="Background video player"
      allow="autoplay; encrypted-media;"
      referrerpolicy="strict-origin-when-cross-origin"
    />
  </div>
</template>

<style scoped>
.c-video-background__container {
  overflow: hidden;
  width: 100%;
  position: absolute;
  container-type: size;

  video {
    object-fit: cover;
  }

  & iframe,
  & video {
    position: absolute;
    /* keep video centered both vertically and horizontally */
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: 100%;
    height: 100%;

    /* force aspect ratio based on parent container */
    @container (min-aspect-ratio: 16/9) {
      height: 56.25cqw; /* 9/16 * 100 (cqw = percentage of container) */
    }

    @container (max-aspect-ratio: 16/9) {
      width: 177.78cqh; /* 16/9 * 100 (cqh = percentage of container) */
    }
  }
}

.c-video-background__fallback {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
